/**
 * Полоса важного над лентой.
 *
 * Вместо заголовка «ALLSWIPES» здесь то, ради чего человек открыл приложение:
 * ближайший розыгрыш с обратным отсчётом, перенесённые сроки, новые акции.
 * Слайды собираются из той же ленты, что уже пришла, — выдуманных новостей тут
 * нет и быть не может: каждый слайд ведёт на страницу, где написанное можно
 * проверить.
 *
 * Листается само раз в семь секунд, останавливается под курсором и при
 * фокусе с клавиатуры, а при `prefers-reduced-motion` не листается вовсе —
 * движущийся блок в самом верху экрана иначе мешает читать.
 */
import type { Feed, FeedCard } from '@allswipes/client';
import { Countdown, Icon, num, type IconName } from '@allswipes/ui';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { usePlural, useT } from '@allswipes/i18n';

interface Slide {
  key: string;
  /** Короткая метка сверху: что это за событие. */
  eyebrow: string;
  title: string;
  note?: string;
  /** Отсчёт в правой колонке — там, где важно именно время. */
  countdownTo?: string;
  /** Подпись к отсчёту: до чего именно считаем. */
  countdownLabel?: string;
  cta: string;
  to: string;
  icon: IconName;
}

/**
 * Иконка в метке слайда.
 *
 * `art` в слайде оператора остаётся как вид события, но рисунка за ним больше
 * нет: абстрактная геометрия ничего не объясняла и спорила с текстом.
 */
const ART_ICONS: Record<string, IconName> = {
  draw: 'trophy',
  moved: 'clock',
  fresh: 'flame',
  fair: 'shield',
};

const WEEK = 7 * 24 * 60 * 60 * 1000;

type T = (text: string, vars?: Record<string, string | number>) => string;
type Plural = (n: number, one: string, few: string, many: string) => string;

// Слайды собираются вне компонента, поэтому перевод приходит аргументом:
// хук можно звать только внутри, а список строится из ленты один раз.
function buildSlides(feed: Feed, t: T, plural: Plural): Slide[] {
  const slides: Slide[] = [];

  // Слайды оператора идут первыми: это то, что платформа решила сказать
  // сегодня. Всё остальное ниже собирается из самой ленты.
  for (const s of feed.slides ?? []) {
    slides.push({
      key: `op:${s.id}`,
      eyebrow: s.eyebrow || t('Платформа'),
      title: s.title,
      note: s.note,
      cta: s.cta_text || t('Открыть'),
      to: s.href || '/',
      icon: ART_ICONS[s.art] ?? 'sparkle',
    });
  }

  const next = feed.next_draw;
  if (next?.campaign.draw_at) {
    slides.push({
      key: 'draw',
      eyebrow: t('Ближайший розыгрыш'),
      title: next.campaign.prize_title,
      note: `${next.brand?.name ?? ''} · ${t('{n} {tickets} в игре', {
        n: num(next.campaign.tickets),
        tickets: plural(next.campaign.tickets, 'билет', 'билета', 'билетов'),
      })}`,
      countdownTo: next.campaign.draw_at,
      countdownLabel: t('до розыгрыша'),
      cta: t('Смотреть акцию'),
      to: `/c/${next.campaign.id}`,
      icon: 'trophy',
    });
  }

  // Перенос срока — самая неприятная новость, и поэтому она стоит в полосе
  // важного, а не прячется в карточке: иначе сдвиг выглядит как лохотрон.
  const moved = feed.active.find((c) => (c.campaign.extensions?.length ?? 0) > 0);
  if (moved) {
    const history = moved.campaign.extensions ?? [];
    const last = history[history.length - 1];
    slides.push({
      key: 'moved',
      eyebrow: t('Срок перенесён'),
      title: moved.campaign.prize_title,
      note:
        last?.reason ||
        t('Участников меньше объявленного минимума — дата сдвинута, условия те же.'),
      cta: t('Посмотреть историю переносов'),
      to: `/c/${moved.campaign.id}`,
      icon: 'clock',
    });
  }

  const fresh = countFresh(feed.active);
  if (fresh > 1) {
    slides.push({
      key: 'fresh',
      eyebrow: t('На этой неделе'),
      title: `${num(fresh)} ${plural(fresh, 'новая акция', 'новые акции', 'новых акций')}`,
      note: t('Запустились за последние семь дней — коды уже в магазинах.'),
      cta: t('Показать новые'),
      to: '/?sort=new',
      icon: 'flame',
    });
  }

  // Последний слайд — про проверяемость. Он ведёт в отчёт состоявшегося
  // розыгрыша, если такой есть: «проверь сам» без страницы, где это можно
  // сделать, — просто лозунг.
  const done = feed.finished.find((c) => c.campaign.draw_at);
  slides.push({
    key: 'fair',
    eyebrow: t('Почему этому можно верить'),
    title: t('Любой розыгрыш пересчитывается в браузере'),
    note: t(
      'Список билетов и сид публикуются до розыгрыша. Результат проверяется без нас — формулой, а не словом.',
    ),
    cta: done ? t('Открыть отчёт розыгрыша') : t('Где это видно'),
    to: done ? `/draw/${done.campaign.id}` : '/my',
    icon: 'shield',
  });

  return slides;
}

function countFresh(cards: FeedCard[]): number {
  const since = Date.now() - WEEK;
  return cards.filter((c) => {
    const started = Date.parse(c.campaign.start_at);
    return Number.isFinite(started) && started >= since;
  }).length;
}

gsap.registerPlugin(useGSAP);

export function HeroRail({ feed }: { feed: Feed }) {
  const t = useT();
  const plural = usePlural();
  const slides = useMemo(() => buildSlides(feed, t, plural), [feed, t, plural]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const rail = useRef<HTMLDivElement>(null);

  // Лента могла смениться (другая сфера, другая страна) — слайдов стало
  // меньше, и прежний индекс указывает в пустоту.
  useEffect(() => setIndex((i) => (i < slides.length ? i : 0)), [slides.length]);

  useEffect(() => {
    if (paused || slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setTimeout(() => setIndex((i) => (i + 1) % slides.length), 7000);
    return () => clearTimeout(timer);
  }, [index, paused, slides.length]);

  // Смена слайда.
  //
  // Двигается только середина — метка, отсчёт, заголовок и пояснение. Кнопка
  // и точки стоят на месте: если бы ездило всё, кнопка уходила бы из-под
  // курсора ровно в тот момент, когда на неё целятся.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        // fromTo, а не from: конечное состояние задано явно. Прерванный на
        // полпути from оставляет элемент невидимым — а прерывают его тут
        // постоянно, автопрокруткой поверх клика по стрелке.
        const appear = (selector: string, delay: number) =>
          gsap.fromTo(
            selector,
            { autoAlpha: 0, y: 10 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.3,
              delay,
              stagger: 0.06,
              ease: 'power2.out',
              overwrite: 'auto',
              clearProps: 'transform',
            },
          );
        appear('.hr-mid > *', 0);
        appear('.hr-side > *', 0.06);
      });
      return () => mm.revert();
    },
    { scope: rail, dependencies: [index], revertOnUpdate: true },
  );

  const slide = slides[Math.min(index, slides.length - 1)];
  if (!slide) return null;

  return (
    <section
      className="hrail"
      aria-roledescription={t('карусель')}
      aria-label={t('Важное')}
      ref={rail}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % slides.length);
        if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + slides.length) % slides.length);
      }}
    >
      {/* Две колонки: слева текст слайда, справа — время и действие. Высоты
          строк заданы жёстко, поэтому слайд с отсчётом и слайд без него
          занимают одно и то же место, и полоса не дёргается при смене. */}
      <div className="hr-body" key={slide.key}>
        <div className="hr-mid">
          {/* Подпись слайда — строка текста, а не чип со значком. Что это
              за событие, говорят сами слова; рамка, заливка и иконка рядом
              ничего не добавляют и делают полосу похожей на рекламный блок. */}
          <span className="hr-eye">{t(slide.eyebrow)}</span>
          <h1 className="hr-title">{t(slide.title)}</h1>
          <p className="hr-note">{t(slide.note ?? '')}</p>
        </div>

        <div className="hr-side">
          {/* Слот времени всегда на месте: у слайда без отсчёта он пустой,
              и кнопка от этого не переезжает. */}
          <div className="hr-cd">
            {slide.countdownTo && (
              <>
                <Countdown to={slide.countdownTo} />
                <span className="l">{t(slide.countdownLabel ?? 'осталось')}</span>
              </>
            )}
          </div>

          {slide.to.startsWith('http') ? (
            <a href={slide.to} className="btn pri">
              {t(slide.cta)}
            </a>
          ) : (
            <Link to={slide.to} className="btn pri">
              {t(slide.cta)}
            </Link>
          )}
        </div>
      </div>

      {slides.length > 1 && (
        <div className="hr-nav">
          <div className="hr-dots" role="tablist" aria-label={t('Слайды')}>
            {slides.map((s, i) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={s.eyebrow}
                className={i === index ? 'on' : undefined}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>

          {/* Стрелки, а не только точки: точками листают, когда знают, что
              слайдов несколько, — стрелка это показывает сама. */}
          <div className="hr-arrows">
            <button
              type="button"
              aria-label={t('Предыдущее')}
              onClick={() => setIndex((i) => (i - 1 + slides.length) % slides.length)}
            >
              <Icon name="chevronLeft" size={14} />
            </button>
            <button
              type="button"
              aria-label={t('Следующее')}
              onClick={() => setIndex((i) => (i + 1) % slides.length)}
            >
              <Icon name="chevronRight" size={14} />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
